import Bull from 'bull'
import type { Queue } from 'bull'
import { createLogger } from '../../common/utils/logger.js'
import { bullRedisConfig } from '../config/redisConfig.js'

const logger = createLogger('AIQueue')

// Keep real queue for prod and opt-in dev; still the default unless NODE_ENV=test without opt-in.
const shouldUseRealQueue = process.env.NODE_ENV !== 'test' || process.env.ENABLE_REAL_AI_QUEUE === 'true'

const createMockQueue = (): Queue => {
  const noop = () => undefined
  const noopAsync = async () => undefined
  let lastJobId = 0

  const buildJob = (id: string) => ({
    id,
    returnvalue: null,
    finishedOn: Date.now(),
    attemptsMade: 0,
    progress: () => 0,
    getState: async () => 'waiting',
    remove: noopAsync
  })

  return {
    process: noop as any,
    add: async () => buildJob(`test-${++lastJobId}`) as any,
    on: noop as any,
    off: noop as any,
    getJob: async (id: string) => buildJob(id) as any,
    getJobCounts: async () => ({
      waiting: 0,
      active: 0,
      completed: 0,
      failed: 0,
      delayed: 0,
      paused: 0
    }),
    getCompleted: async () => [],
    getFailed: async () => [],
    close: noopAsync as any
  } as unknown as Queue
}

let aiQueue: Queue | null = null
let cleanupInterval: NodeJS.Timeout | null = null
let idleTimer: NodeJS.Timeout | null = null
const idleTimeoutMs = process.env.NODE_ENV === 'production' ? 10 * 60 * 1000 : 30_000 // 10min prod, 30s dev

const attachEventHandlers = (queue: Queue) => {
  const completedHandler = (job: Bull.Job) => {
    logger.info('AI job completed', { jobId: job.id, name: job.name })
    resetIdleTimer()
  }

  const failedHandler = (job: Bull.Job | undefined, err: Error) => {
    logger.error('AI job failed', { jobId: job?.id, name: job?.name, error: err.message, stack: err.stack })
    resetIdleTimer()
  }

  const globalCompletedHandler = async (jobId: string) => {
    try {
      const job = await queue.getJob(jobId)
      if (job) {
        logger.debug('Completed job retained for status lookup', { jobId })
      }
    } catch (err) {
      logger.debug('Failed to cleanup job', { jobId, error: err instanceof Error ? err.message : 'Unknown error' })
    }
  }

  const globalFailedHandler = async (jobId: string) => {
    try {
      const job = await queue.getJob(jobId)
      if (job && job.attemptsMade >= (job.opts.attempts || 3)) {
        await job.remove()
        logger.info('Cleaned up failed job after max attempts', { jobId, attempts: job.attemptsMade })
      }
    } catch (err) {
      logger.debug('Failed to cleanup failed job', { jobId, error: err instanceof Error ? err.message : 'Unknown error' })
    }
    resetIdleTimer()
  }

  queue.on('completed', completedHandler)
  queue.on('failed', failedHandler)
  queue.on('global:completed', globalCompletedHandler)
  queue.on('global:failed', globalFailedHandler)
  queue.on('waiting', resetIdleTimer)
  queue.on('active', resetIdleTimer)
  queue.on('stalled', resetIdleTimer)

  return {
    completedHandler,
    failedHandler,
    globalCompletedHandler,
    globalFailedHandler
  }
}

let handlers:
  | {
      completedHandler: (job: Bull.Job) => void
      failedHandler: (job: Bull.Job | undefined, err: Error) => void
      globalCompletedHandler: (jobId: string) => Promise<void>
      globalFailedHandler: (jobId: string) => Promise<void>
    }
  | null = null

const startCleanupInterval = () => {
  if (!aiQueue || cleanupInterval || process.env.NODE_ENV === 'test') return
  cleanupInterval = setInterval(async () => {
    try {
      const completed = await aiQueue!.getCompleted(0, 100)
      let cleaned = 0

      for (const job of completed) {
        const age = Date.now() - (job.finishedOn || Date.now())
        if (age > 3600000) {
          await job.remove()
          cleaned++
        }
      }

      const failed = await aiQueue!.getFailed(0, 100)
      for (const job of failed) {
        if (job.attemptsMade >= (job.opts.attempts || 3)) {
          await job.remove()
          cleaned++
        }
      }

      if (cleaned > 0) {
        logger.info('Periodic cleanup removed old jobs from queue', { cleanedCount: cleaned })
      }
    } catch (err) {
      logger.error('Failed to clean up old jobs', { error: err instanceof Error ? err.message : 'Unknown error' })
    }
  }, 300000)
}

const resetIdleTimer = () => {
  if (idleTimer) {
    clearTimeout(idleTimer)
  }
  if (!aiQueue || !shouldUseRealQueue) return
  idleTimer = setTimeout(async () => {
    logger.info('AI queue idle timeout reached, closing to stop polling')
    await closeAIQueue()
  }, idleTimeoutMs)
}

/**
 * Lazy getter to avoid opening Redis when unused.
 */
export const getAIQueue = (): Queue => {
  if (aiQueue) return aiQueue

  aiQueue = shouldUseRealQueue
    ? new Bull('ai-jobs', {
        redis: bullRedisConfig,
        settings: {
          stalledInterval: 30000,
          maxStalledCount: 1,
          lockDuration: 60000,
          lockRenewTime: 30000,
          guardInterval: process.env.NODE_ENV === 'production' ? 5000 : 300000,
          retryProcessDelay: 10000
        },
        limiter: {
          max: process.env.NODE_ENV === 'production' ? 10 : 2,
          duration: 5000
        }
      })
    : createMockQueue()

  if (shouldUseRealQueue) {
    handlers = attachEventHandlers(aiQueue)
    startCleanupInterval()
    resetIdleTimer()
  }

  return aiQueue
}

/**
 * Cleanup function to remove event listeners and close queue
 * Call this on server shutdown to prevent memory leaks
 */
export async function closeAIQueue(): Promise<void> {
  if (cleanupInterval) {
    clearInterval(cleanupInterval)
    cleanupInterval = null
  }
  if (idleTimer) {
    clearTimeout(idleTimer)
    idleTimer = null
  }

  if (aiQueue && shouldUseRealQueue) {
    if (handlers) {
      aiQueue.off('completed', handlers.completedHandler)
      aiQueue.off('failed', handlers.failedHandler)
      aiQueue.off('global:completed', handlers.globalCompletedHandler)
      aiQueue.off('global:failed', handlers.globalFailedHandler)
      handlers = null
    }
    await aiQueue.close()
    logger.info('AI Queue closed and event listeners removed')
  }

  aiQueue = null
}
